import { useState, useRef, useCallback } from "react";
import { createClient } from "@supabase/supabase-js";

/* ─── PALETTE YOMA ─────────────────────────────────── */
const C = {
  bg: "#F8F4EE", bgCard: "#FFFFFF", bgDeep: "#F2EBE0",
  taupe: "#8B7361", taupeLight: "#C4B5A5", taupeDeep: "#3D2E20",
  beige: "#EDE3D5", gold: "#C09A5B", goldLight: "#E8D4B0", goldDeep: "#9A7A3A",
  text: "#2E2418", muted: "#9A8778", border: "#DDD0C0",
  success: "#5C8A5C", successLight: "#EAF2EA",
  alert: "#B85C3A", alertLight: "#FAF0EB",
  locked: "#C8BDB5", lockedBg: "#F5F0EB",
  info: "#4A7A9B", infoLight: "#EBF2F8",
  white: "#FFFFFF",
};

/* ─── FONTS ─────────────────────────────────────────── */
const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500;600&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
    body{background:${C.bg};font-family:'DM Sans',sans-serif}
    input,textarea,select{font-family:'DM Sans',sans-serif}
    ::-webkit-scrollbar{width:4px}
    ::-webkit-scrollbar-track{background:${C.beige}}
    ::-webkit-scrollbar-thumb{background:${C.taupeLight};border-radius:2px}
    @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
    @keyframes shimmer{0%,100%{opacity:.4}50%{opacity:1}}
    @keyframes unlock{0%{transform:scale(1)}50%{transform:scale(1.08)}100%{transform:scale(1)}}
    .step-content{animation:fadeIn .35s ease}
    .pulse{animation:shimmer 1.4s infinite}
  `}</style>
);

/* ─── CONFIG ÉTAPES ─────────────────────────────────── */
const ETAPES = [
  {
    id: 1, code: "ESQ", label: "Diagnostic & Esquisse", short: "Diagnostic",
    subtitle: "Relevé · Cahier des charges · Esquisse",
    icon: "◈", color: C.gold,
    livrable: "Note d'Esquisse (ESQ)",
    validationLabel: "Valider l'Esquisse — Passer à la Conception",
    description: "Relevé de l'existant, entretien approfondi avec le client, création des premiers plans de zoning et de la planche d'ambiance."
  },
  {
    id: 2, code: "APS/APD", label: "Conception Détaillée", short: "Conception",
    subtitle: "APS · APD · Estimation budgétaire",
    icon: "◉", color: "#7A9B8A",
    livrable: "Avant-Projet Sommaire & Définitif",
    validationLabel: "Valider la Conception — Passer au Dossier Technique",
    description: "Choix du scénario, plans 2D cotés, premières vues 3D, validation des matériaux et estimation budgétaire globale."
  },
  {
    id: 3, code: "DCE", label: "Dossier Technique", short: "Dossier Tech.",
    subtitle: "DCE · Carnet de détails · Book Déco",
    icon: "◇", color: "#8A7A9B",
    livrable: "DCE + Book Déco complet",
    validationLabel: "Valider le Dossier — Lancer la Consultation",
    description: "Plans techniques pour les artisans, carnet de menuiserie sur mesure et shopping list complète FF&E."
  },
  {
    id: 4, code: "AO", label: "Consultation & Commandes", short: "Commandes",
    subtitle: "Appel d'offres · Marchés · FF&E",
    icon: "◎", color: "#9B8A5A",
    livrable: "Marchés signés + Commandes lancées",
    validationLabel: "Valider les Marchés — Démarrer le Chantier",
    description: "Analyse des devis artisans, signature des marchés de travaux et lancement des commandes mobilier (délais 8–12 semaines)."
  },
  {
    id: 5, code: "DET", label: "Suivi de Chantier", short: "Chantier",
    subtitle: "DET · Comptes-rendus · Menuiserie",
    icon: "◐", color: "#5A8A9B",
    livrable: "DET + CR hebdomadaires",
    validationLabel: "Clôturer le Chantier — Passer à l'Installation",
    description: "Visites régulières sur site, coordination des corps d'état, rapports hebdomadaires au client et pose de la menuiserie."
  },
  {
    id: 6, code: "DOE", label: "Installation & Mise en Scène", short: "Livraison",
    subtitle: "Réception · Styling · Remise des clés",
    icon: "◆", color: C.success,
    livrable: "PV de réception + DOE",
    validationLabel: "Clôturer le Projet — Remise des Clés",
    description: "Réception des travaux, livraison et montage du mobilier, styling final et remise du DOE au client."
  },
];

/* ─── HELPERS API ────────────────────────────────────── */
async function callAI(system, user, images = []) {
  const { supabase } = await import("../supabaseClient");
  
  const { data, error } = await supabase.functions.invoke("generate-ai", {
    body: { system, user, images },
  });
  
  if (error) throw error;
  return data?.data?.content?.[0]?.text || 'Erreur génération';
}

/* ─── UI PRIMITIVES ──────────────────────────────────── */
const Label = ({ children, required }) => (
  <div style={{ fontFamily: "'DM Sans'", fontSize: "10px", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: C.muted, marginBottom: "6px" }}>
    {children}{required && <span style={{ color: C.alert, marginLeft: "3px" }}>*</span>}
  </div>
);

const Input = ({ value, onChange, placeholder, multiline, rows = 3, style: s }) => {
  const base = { width: "100%", border: `1px solid ${C.border}`, borderRadius: "3px", padding: "9px 12px", background: C.bg, color: C.text, outline: "none", fontSize: "13px", fontFamily: "'DM Sans'", transition: "border-color .2s", ...s };
  return multiline
    ? <textarea rows={rows} style={base} value={value} onChange={onChange} placeholder={placeholder} onFocus={e => e.target.style.borderColor = C.gold} onBlur={e => e.target.style.borderColor = C.border} />
    : <input style={base} value={value} onChange={onChange} placeholder={placeholder} onFocus={e => e.target.style.borderColor = C.gold} onBlur={e => e.target.style.borderColor = C.border} />;
};

const Sel = ({ value, onChange, options }) => (
  <select value={value} onChange={onChange} style={{ width: "100%", border: `1px solid ${C.border}`, borderRadius: "3px", padding: "9px 12px", background: C.bg, color: C.text, fontSize: "13px", fontFamily: "'DM Sans'", outline: "none" }}>
    {options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
  </select>
);

const Card = ({ children, style: s }) => (
  <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: "4px", padding: "24px", ...s }}>
    {children}
  </div>
);

const Grid = ({ cols = 2, children, gap = "16px" }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap }}>{children}</div>
);

const Field = ({ label, children, required }) => (
  <div><Label required={required}>{label}</Label>{children}</div>
);

const SecTitle = ({ children, sub }) => (
  <div style={{ marginBottom: "20px" }}><div style={{ fontFamily: "'Playfair Display', serif", fontSize: "20px", color: C.taupeDeep, fontWeight: 500 }}>{children}</div>
    {sub && <div style={{ fontFamily: "'DM Sans'", fontSize: "11px", color: C.muted, marginTop: "4px", letterSpacing: "0.05em" }}>{sub}</div>}
  </div>
);

const AIBox = ({ content, loading, title = "Analyse IA — YOMA DESIGN" }) => (
  <div style={{ background: `linear-gradient(135deg, ${C.beige}, #F8F0E8)`, border: `1px solid ${C.goldLight}`, borderLeft: `4px solid ${C.gold}`, borderRadius: "3px", padding: "22px", marginTop: "20px" }}><div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}><div style={{ width: "6px", height: "6px", borderRadius: "50%", background: C.gold }} /><span style={{ fontFamily: "'DM Sans'", fontSize: "9px", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: C.goldDeep }}>{title}</span></div>
    {loading
      ? <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          {[0, 1, 2].map(i => <div key={i} className="pulse" style={{ width: "7px", height: "7px", borderRadius: "50%", background: C.gold, animationDelay: `${i * 0.2}s` }} />)}
          <span style={{ fontFamily: "'DM Sans'", fontSize: "12px", color: C.muted, marginLeft: "6px" }}>Génération en cours…</span></div>
      : <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "14px", color: C.taupeDeep, lineHeight: 1.9, whiteSpace: "pre-wrap" }}>{content}</div>
    }
  </div>
);

const Btn = ({ children, onClick, variant = "primary", disabled, small, style: s }) => {
  const vars = {
    primary: { background: C.taupeDeep, color: C.white },
    gold: { background: C.gold, color: C.white },
    ghost: { background: "transparent", color: C.taupe, border: `1px solid ${C.border}` },
    success: { background: C.success, color: C.white },
    danger: { background: C.alert, color: C.white },
    locked: { background: C.lockedBg, color: C.locked, border: `1px solid ${C.border}`, cursor: "not-allowed" },
  };
  return (
    <button
      onClick={disabled ? undefined : onClick}
      style={{
        fontFamily: "'DM Sans'", fontWeight: 600, letterSpacing: "0.08em",
        fontSize: small ? "10px" : "11px", padding: small ? "7px 14px" : "11px 24px",
        border: "none", borderRadius: "3px", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1, transition: "all .2s", textTransform: "uppercase",
        ...vars[variant], ...s,
      }}
    >{children}</button>
  );
};

const Badge = ({ children, color = C.gold }) => (
  <span style={{ background: color + "20", color, border: `1px solid ${color}50`, borderRadius: "2px", padding: "2px 8px", fontFamily: "'DM Sans'", fontSize: "9px", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase" }}>
    {children}
  </span>
);

/* ─── LIVRABLE BADGE ─────────────────────────────────── */
const LivrablerBadge = ({ code, label, generated }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", background: generated ? C.successLight : C.bgDeep, border: `1px solid ${generated ? C.success + "50" : C.border}`, borderRadius: "3px" }}><div style={{ fontFamily: "'Playfair Display'", fontSize: "18px", color: generated ? C.success : C.taupeLight, fontWeight: 700 }}>{code}</div><div><div style={{ fontFamily: "'DM Sans'", fontSize: "11px", fontWeight: 600, color: generated ? C.success : C.muted }}>{label}</div><div style={{ fontFamily: "'DM Sans'", fontSize: "10px", color: C.muted }}>{generated ? "✓ Généré" : "En attente"}</div></div></div>
);

/* ─── UPLOAD ZONE ────────────────────────────────────── */
function UploadZone({ label, files, onFiles, accept = "image/*", multiple = true }) {
  const ref = useRef();
  const handle = useCallback((fl) => {
    const ps = [...fl].map(f => new Promise(res => {
      const r = new FileReader();
      r.onload = () => res({ id: Date.now() + Math.random(), name: f.name, url: r.result, type: f.type, data: r.result.split(",")[1] });
      r.readAsDataURL(f);
    }));
    Promise.all(ps).then(imgs => onFiles(prev => [...(prev || []), ...imgs]));
  }, [onFiles]);

  return (
    <div><Label>{label}</Label><div style={{ border: `2px dashed ${C.border}`, borderRadius: "3px", padding: "20px", textAlign: "center", cursor: "pointer", background: C.bg, transition: "border-color .2s" }}
        onClick={() => ref.current.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); handle(e.dataTransfer.files); }}
        onMouseEnter={e => e.currentTarget.style.borderColor = C.gold}
        onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
      ><div style={{ fontFamily: "'Playfair Display'", fontSize: "22px", color: C.taupeLight }}>◎</div><div style={{ fontFamily: "'DM Sans'", fontSize: "11px", color: C.muted, marginTop: "6px" }}>Glisser ou cliquer — {accept}</div><input ref={ref} type="file" accept={accept} multiple={multiple} style={{ display: "none" }} onChange={e => handle(e.target.files)} /></div>
      {files?.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "10px" }}>
          {files.map(f => (
            <div key={f.id} style={{ position: "relative", borderRadius: "3px", overflow: "hidden", border: `1px solid ${C.border}` }}>
              {f.type?.startsWith("image") ? (
                <img src={f.url} alt={f.name} style={{ width: "80px", height: "60px", objectFit: "cover", display: "block" }} />
              ) : (
                <div style={{ width: "80px", height: "60px", background: C.bgDeep, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", color: C.muted, padding: "4px", textAlign: "center" }}>{f.name}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   ÉTAPE 1 — Diagnostic & Esquisse
══════════════════════════════════════════════════════ */
function Etape1({ data, set }) {
  const [loading, setLoading] = useState(false);

  const PIECES_OPTIONS = [
    "Salon",
    "Cuisine",
    "Salle de bain",
    "Chambre",
    "Salle à manger",
    "Bureau",
    "Dressing",
    "Cave/Sous-sol",
    "Autre",
  ];

  const EXTERIEUR_OPTIONS = [
    "Terrasse",
    "Jardin",
    "Piscine",
    "Pool house",
    "Garage",
    "Entrée extérieure",
    "Autre extérieur",
  ];

  const OBJECTIFS_OPTIONS = [
    "Transformation esthétique",
    "Augmenter la valeur",
    "Préparer à la revente",
    "Amélioration fonctionnalité",
    "Optimisation de l'espace",
  ];

  const AMBIANCES = ["Chaleureuse", "Tamisée", "Lumineuse", "Contrastée"];

  const [loadingPlans, setLoadingPlans] = useState(false);

  const handleInspirationUpload = (piece, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const toRead = files;
    const readers = toRead.map(
      (f) =>
        new Promise((res) => {
          const r = new FileReader();
          r.onload = () => {
            const result = r.result || "";
            const str = typeof result === "string" ? result : "";
            const base64 = str.includes(",") ? str.split(",")[1] : str;
            res({
              id: Date.now() + Math.random(),
              name: f.name,
              url: str,
              type: f.type,
              data: base64,
              note: "",
            });
          };
          r.readAsDataURL(f);
        })
    );

    Promise.all(readers).then((newImgs) => {
      set((d) => {
        const all = d.photosInspiration || {};
        const current = all[piece] || [];
        const merged = [...current, ...newImgs].slice(0, 3);
        return {
          ...d,
          photosInspiration: {
            ...all,
            [piece]: merged,
          },
        };
      });
    });
  };

  async function generateLien() {
    try {
      const { supabase } = await import("../supabaseClient");

      // 1. Créer l'entrée dans pre_consultations
      const { data: record, error } = await supabase
        .from("pre_consultations")
        .insert({
          client_name: data.clientNom,
          client_email: data.clientEmail,
          status: "en_attente",
        })
        .select()
        .single();

      if (error) {
        alert("Erreur: " + error.message);
        return;
      }

      // 2. Générer le lien
      const baseUrl = 'https://app.yoma.design';
      const lien = `${baseUrl}/#/preconsultation/${record.token}`;

      // 3. Envoyer email via Edge Function send-email
      const emailResult = await supabase.functions.invoke("send-email", {
        body: {
          to: data.clientEmail,
          subject: `${data.clientNom} — Votre questionnaire YOMA DESIGN`,
          html: `
        <div style="font-family: Georgia, serif; max-width: 600px; 
          margin: 0 auto; padding: 40px; background: #FAF7F2;"><div style="text-align: center; margin-bottom: 32px;"><h1 style="font-size: 28px; color: #3D2E20; 
              letter-spacing: 0.15em; font-weight: 300;">
              YOMA DESIGN
            </h1><p style="color: #9A8778; font-size: 12px; 
              letter-spacing: 0.2em; text-transform: uppercase;">
              Architecture Intérieure — Marrakech
            </p></div><p style="color: #3D2E20; font-size: 16px; line-height: 1.8;">
            Cher(e) <strong>${data.clientNom}</strong>,
          </p><p style="color: #5C4A35; font-size: 15px; line-height: 1.9; 
            margin-top: 16px;">
            Nous vous remercions de l'intérêt que vous portez à YOMA DESIGN. 
            Afin de préparer au mieux notre première rencontre et de vous 
            proposer un accompagnement sur mesure, nous vous invitons à 
            remplir notre questionnaire de pré-consultation.
          </p><p style="color: #5C4A35; font-size: 15px; line-height: 1.9;">
            Ce questionnaire nous permettra de comprendre votre projet, 
            vos goûts, vos contraintes et vos aspirations avant notre échange.
          </p><div style="text-align: center; margin: 40px 0;"><a href="${lien}" 
              style="background: #C09A5B; color: white; 
                padding: 16px 40px; text-decoration: none; 
                font-family: sans-serif; font-size: 13px; 
                font-weight: 600; letter-spacing: 0.1em; 
                text-transform: uppercase; border-radius: 3px;">
              Remplir mon questionnaire
            </a></div><p style="color: #9A8778; font-size: 12px; line-height: 1.7; 
            text-align: center; margin-top: 16px;">
            Ou copiez ce lien dans votre navigateur :<br/><span style="color: #C09A5B;">${lien}</span></p><div style="border-top: 1px solid #DDD0C0; margin-top: 40px; 
            padding-top: 24px; text-align: center;"><p style="color: #9A8778; font-size: 12px; line-height: 1.8;">
              YOMA DESIGN SARL AU — Marrakech, Maroc<br/>
              contact@yoma.design
            </p></div></div>
      `,
        },
      });
      console.log("Email result:", emailResult);

      // 4. Afficher confirmation avec le lien
      set((d) => ({
        ...d,
        lienGenere: lien,
        tokenGenere: record.token,
      }));
    } catch (e) {
      alert("Erreur: " + (e?.message || e));
    }
  }

  const generate = async () => {
    setLoading(true);
    const images = (data.photos || []).slice(0, 4).map(i => ({ type: i.type, data: i.data }));
    const sys = `Tu es YOMA DESIGN, cabinet d'architecture intérieure premium à Marrakech.
Rédige une Note d'Esquisse (ESQ) professionnelle et élégante.
Structure imposée :
1. IDENTIFICATION DU PROJET
2. SYNTHÈSE DU CAHIER DES CHARGES
3. CONTRAINTES & OPPORTUNITÉS IDENTIFIÉES
4. ORIENTATIONS STYLISTIQUES PROPOSÉES
5. ZONING PRÉLIMINAIRE (description textuelle des espaces)
6. PROCHAINES ÉTAPES — APS
Ton : directeur artistique de haut niveau, précis, rassurant.`;
    const user = `Client : ${data.client} | Type : ${data.typeProjet} | Surface : ${data.surface} m² | Budget : ${data.budget} MAD
Style souhaité : ${data.style} | Pièces : ${data.pieces}
Objectifs du projet : ${(data.objectifsProjet || []).join(", ")}
Incontournables : ${data.incontournables || ""}
Éléments à exclure : ${data.aExclure || ""}
Contraintes relevées : ${data.contraintes}
Cotes / relevé : ${data.releve}
${images.length ? `${images.length} photo(s) du site jointes.` : ""}`;
    const result = await callAI(sys, user, images);
    set(d => ({ ...d, esq: result }));
    setLoading(false);
  };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* Bloc Envoyer le questionnaire au client */}
      <Card style={{ background: C.goldLight + "20", border: `1px solid ${C.goldLight}` }}><div style={{ display: "flex", flexDirection: "column", gap: "12px" }}><SecTitle sub="Générez un lien unique de pré-consultation à envoyer à votre client">
            Envoyer le questionnaire au client
          </SecTitle><Grid cols={2}><Field label="Nom du client"><Input
                value={data.clientNom || ""}
                onChange={(e) =>
                  set((d) => ({ ...d, clientNom: e.target.value }))
                }
                placeholder="Nom complet du client"
              /></Field><Field label="Email du client"><Input
                value={data.clientEmail || ""}
                onChange={(e) =>
                  set((d) => ({ ...d, clientEmail: e.target.value }))
                }
                placeholder="email@client.com"
              /></Field></Grid><div style={{ display: "flex", justifyContent: "flex-start", marginTop: "4px" }}><Btn
              variant="gold"
              onClick={generateLien}
              disabled={!data.clientNom || !data.clientEmail || loading}
            >
Générer & Envoyer le lien
            </Btn></div>

          {data.lienGenere && (
            <Card
              style={{
                marginTop: "10px",
                background: C.successLight,
                border: `1px solid ${C.success}40`,
              }}
            ><div
                style={{
                  fontFamily: "'DM Sans'",
                  fontSize: "12px",
                  color: C.success,
                  fontWeight: 600,
                  marginBottom: "6px",
                }}
              >
                ✓ Lien envoyé à {data.clientEmail}
              </div><div
                style={{
                  fontFamily: "'DM Sans'",
                  fontSize: "12px",
                  color: C.gold,
                  wordBreak: "break-all",
                  marginBottom: "8px",
                }}
              ><a
                  href={data.lienGenere}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: C.gold, textDecoration: "none" }}
                >
                  {data.lienGenere}
                </a></div><div style={{ display: "flex", justifyContent: "flex-start" }}><Btn
                  small
                  variant="ghost"
                  onClick={() => {
                    if (navigator.clipboard?.writeText) {
                      navigator.clipboard.writeText(data.lienGenere);
                    }
                  }}
                >
                  Copier le lien
                </Btn></div><div
                style={{
                  fontFamily: "'DM Sans'",
                  fontSize: "11px",
                  color: C.muted,
                  marginTop: "6px",
                }}
              >
                Le client a reçu un email avec son questionnaire personnalisé.
              </div></Card>
          )}
        </div></Card><Card><SecTitle sub="Identification complète du projet et du client">Relevé & Identification</SecTitle><div style={{ display: "flex", flexDirection: "column", gap: "14px" }}><Grid cols={2}><Field label="Nom du client" required><Input value={data.client || ""} onChange={e => set(d => ({ ...d, client: e.target.value }))} placeholder="M. / Mme …" /></Field><Field label="Type de projet"><Sel value={data.typeProjet || "Villa neuve"} onChange={e => set(d => ({ ...d, typeProjet: e.target.value }))} options={["Villa neuve", "Rénovation complète", "Rénovation partielle", "Riyad", "Appartement", "Espace commercial", "Villa secondaire"]} /></Field></Grid><Grid cols={3}><Field label="Surface totale (m²)" required><Input value={data.surface || ""} onChange={e => set(d => ({ ...d, surface: e.target.value }))} placeholder="Ex : 320" /></Field><Field label="Budget enveloppe (MAD)" required><Input value={data.budget || ""} onChange={e => set(d => ({ ...d, budget: e.target.value }))} placeholder="Ex : 1 200 000" /></Field><Field label="Style souhaité"><Sel value={data.style || "Contemporain"} onChange={e => set(d => ({ ...d, style: e.target.value }))} options={["Contemporain", "Minimaliste", "Oriental moderne", "Classique", "Wabi-sabi", "Art déco", "Bohème luxe", "Scandinave", "Rustique chic", "Japandi", "Mid-Century Modern", "Tropical Luxe", "Méditerranéen", "Industriel Chic"]} /></Field></Grid><Field label="Pièces concernées"><Input value={data.pieces || ""} onChange={e => set(d => ({ ...d, pieces: e.target.value }))} placeholder="Ex : Salon, SàM, 4 chambres, 2 SdB, cuisine, terrasse…" /></Field></div></Card><Card><SecTitle sub="Entretien approfondi — besoins, contraintes, mode de vie">Cahier des Charges</SecTitle><div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          {/* BLOC 1 — Questionnaire de pré-consultation */}
          <div style={{ padding: "14px 16px", background: C.bgDeep, borderRadius: "4px", border: `1px solid ${C.border}` }}><div style={{ fontFamily: "'DM Sans'", fontSize: "11px", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: C.taupeDeep, marginBottom: "10px" }}>
              BLOC 1 — Questionnaire de pré-consultation
            </div>

            {/* Pièces à concevoir avec priorité — INTÉRIEUR */}
            <div style={{ marginBottom: "12px" }}><Label>Pièces à concevoir avec priorité — Intérieur</Label><div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {PIECES_OPTIONS.map((piece) => {
                  const info = (data.piecesPrioritaires && data.piecesPrioritaires[piece]) || {};
                  return (
                    <div key={piece} style={{ display: "flex", alignItems: "center", gap: "6px", background: C.bgCard, borderRadius: "3px", padding: "6px 8px", border: `1px solid ${C.border}` }}><label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: C.text, minWidth: "110px" }}><input
                          type="checkbox"
                          checked={!!info.checked}
                          onChange={e => set(d => {
                            const prev = d.piecesPrioritaires || {};
                            const prevPiece = prev[piece] || {};
                            return {
                              ...d,
                              piecesPrioritaires: {
                                ...prev,
                                [piece]: { ...prevPiece, checked: e.target.checked },
                              },
                            };
                          })}
                        /><span>{piece}</span></label><input
                        type="number"
                        min="1"
                        placeholder="Qté"
                        value={info.quantite || ""}
                        onChange={e => set(d => {
                          const prev = d.piecesPrioritaires || {};
                          const prevPiece = prev[piece] || {};
                          return {
                            ...d,
                            piecesPrioritaires: {
                              ...prev,
                              [piece]: { ...prevPiece, quantite: e.target.value },
                            },
                          };
                        })}
                        style={{ width: "52px", fontSize: "10px", padding: "3px 6px", borderRadius: "3px", border: `1px solid ${C.border}` }}
                      /><input
                        type="text"
                        placeholder="Type / Précision"
                        value={info.type || ""}
                        onChange={e => set(d => {
                          const prev = d.piecesPrioritaires || {};
                          const prevPiece = prev[piece] || {};
                          return {
                            ...d,
                            piecesPrioritaires: {
                              ...prev,
                              [piece]: { ...prevPiece, type: e.target.value },
                            },
                          };
                        })}
                        style={{ width: "150px", fontSize: "10px", padding: "3px 6px", borderRadius: "3px", border: `1px solid ${C.border}` }}
                      /><input
                        type="number"
                        min="1"
                        placeholder="Priorité"
                        value={info.priorite || ""}
                        onChange={e => set(d => {
                          const prev = d.piecesPrioritaires || {};
                          const prevPiece = prev[piece] || {};
                          return {
                            ...d,
                            piecesPrioritaires: {
                              ...prev,
                              [piece]: { ...prevPiece, priorite: e.target.value },
                            },
                          };
                        })}
                        style={{ width: "58px", fontSize: "10px", padding: "3px 6px", borderRadius: "3px", border: `1px solid ${C.border}` }}
                      /></div>
                  );
                })}
              </div></div>

            {/* Pièces EXTÉRIEUR */}
            <div style={{ marginBottom: "12px" }}><Label>EXTÉRIEUR — Espaces à concevoir</Label><div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {EXTERIEUR_OPTIONS.map((piece) => {
                  const info = (data.piecesExterieur && data.piecesExterieur[piece]) || {};
                  return (
                    <div key={piece} style={{ display: "flex", alignItems: "center", gap: "6px", background: C.bgCard, borderRadius: "3px", padding: "6px 8px", border: `1px solid ${C.border}` }}><label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: C.text, minWidth: "140px" }}><input
                          type="checkbox"
                          checked={!!info.checked}
                          onChange={e => set(d => {
                            const prev = d.piecesExterieur || {};
                            const prevPiece = prev[piece] || {};
                            return {
                              ...d,
                              piecesExterieur: {
                                ...prev,
                                [piece]: { ...prevPiece, checked: e.target.checked },
                              },
                            };
                          })}
                        /><span>{piece}</span></label><input
                        type="number"
                        min="1"
                        placeholder="Qté"
                        value={info.quantite || ""}
                        onChange={e => set(d => {
                          const prev = d.piecesExterieur || {};
                          const prevPiece = prev[piece] || {};
                          return {
                            ...d,
                            piecesExterieur: {
                              ...prev,
                              [piece]: { ...prevPiece, quantite: e.target.value },
                            },
                          };
                        })}
                        style={{ width: "52px", fontSize: "10px", padding: "3px 6px", borderRadius: "3px", border: `1px solid ${C.border}` }}
                      /><input
                        type="text"
                        placeholder="Type / Précision"
                        value={info.type || ""}
                        onChange={e => set(d => {
                          const prev = d.piecesExterieur || {};
                          const prevPiece = prev[piece] || {};
                          return {
                            ...d,
                            piecesExterieur: {
                              ...prev,
                              [piece]: { ...prevPiece, type: e.target.value },
                            },
                          };
                        })}
                        style={{ width: "150px", fontSize: "10px", padding: "3px 6px", borderRadius: "3px", border: `1px solid ${C.border}` }}
                      /><input
                        type="number"
                        min="1"
                        placeholder="Priorité"
                        value={info.priorite || ""}
                        onChange={e => set(d => {
                          const prev = d.piecesExterieur || {};
                          const prevPiece = prev[piece] || {};
                          return {
                            ...d,
                            piecesExterieur: {
                              ...prev,
                              [piece]: { ...prevPiece, priorite: e.target.value },
                            },
                          };
                        })}
                        style={{ width: "58px", fontSize: "10px", padding: "3px 6px", borderRadius: "3px", border: `1px solid ${C.border}` }}
                      /></div>
                  );
                })}
              </div></div>

            {/* Objectifs du projet */}
            <div style={{ marginBottom: "12px" }}><Label>Objectifs du projet</Label><div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {OBJECTIFS_OPTIONS.map(obj => {
                  const checked = (data.objectifsProjet || []).includes(obj);
                  return (
                    <label key={obj} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: C.text, background: C.bgCard, borderRadius: "3px", padding: "5px 8px", border: `1px solid ${C.border}` }}><input
                        type="checkbox"
                        checked={checked}
                        onChange={() => set(d => {
                          const prev = d.objectifsProjet || [];
                          const exists = prev.includes(obj);
                          return {
                            ...d,
                            objectifsProjet: exists ? prev.filter(o => o !== obj) : [...prev, obj],
                          };
                        })}
                      /><span>{obj}</span></label>
                  );
                })}
              </div></div>

            {/* Canal de communication & Délai souhaité */}
            <Grid cols={2}><Field label="Canal de communication préféré"><Sel
                  value={data.canalCom || "WhatsApp"}
                  onChange={e => set(d => ({ ...d, canalCom: e.target.value }))}
                  options={["Email", "WhatsApp", "Téléphone", "Application YOMA", "Autre"]}
                /></Field><Field label="Délai souhaité"><Sel
                  value={data.delaiSouhaite || "Moyen terme (6-12 mois)"}
                  onChange={e => set(d => ({ ...d, delaiSouhaite: e.target.value }))}
                  options={[
                    "Urgent (1-3 mois)",
                    "Court terme (3-6 mois)",
                    "Moyen terme (6-12 mois)",
                    "Flexible",
                  ]}
                /></Field></Grid>

            {/* Contact privilégié */}
            <div style={{ marginTop: "10px" }}><Field label="Contact privilégié pour le projet"><Input
                  value={data.contactProjet || ""}
                  onChange={e => set(d => ({ ...d, contactProjet: e.target.value }))}
                  placeholder="Nom, rôle, coordonnées principales…"
                /></Field></div></div>

          {/* BLOC 2 — Incontournables */}
          <div><Label>BLOC 2 — Ce que le client veut ABSOLUMENT</Label><Input
              multiline
              rows={3}
              value={data.incontournables || ""}
              onChange={e => set(d => ({ ...d, incontournables: e.target.value }))}
              placeholder="Intégrer le vaisselier de famille, garder la cheminée existante…"
            /></div>

          {/* BLOC 3 — À exclure */}
          <div><Label>BLOC 3 — Ce que le client ne veut ABSOLUMENT PAS</Label><Input
              multiline
              rows={3}
              value={data.aExclure || ""}
              onChange={e => set(d => ({ ...d, aExclure: e.target.value }))}
              placeholder="Pas de blanc pur, pas de métal noir, pas de style industriel…"
            /></div>

          {/* BLOC 4 — Direction moodboard par pièce */}
          {(PIECES_OPTIONS.filter(p => data.piecesPrioritaires?.[p]?.checked).length > 0 ||
            EXTERIEUR_OPTIONS.filter(p => data.piecesExterieur?.[p]?.checked).length > 0) && (
            <div><Label>BLOC 4 — Direction moodboard par pièce</Label><div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {[...PIECES_OPTIONS, ...EXTERIEUR_OPTIONS]
                  .filter(p => data.piecesPrioritaires?.[p]?.checked || data.piecesExterieur?.[p]?.checked)
                  .map(piece => {
                  const mood = (data.moodboardPieces && data.moodboardPieces[piece]) || {};
                  const photos = (data.photosInspiration && data.photosInspiration[piece]) || [];
                  return (
                    <div key={piece} style={{ border: `1px solid ${C.border}`, borderRadius: "4px", padding: "10px 12px", background: C.bgCard }}><div style={{ fontFamily: "'DM Sans'", fontSize: "11px", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: C.taupeDeep, marginBottom: "8px" }}>
                        {piece}
                      </div><Grid cols={2}><Field label="Palette de couleurs souhaitée"><Input
                            value={mood.palette || ""}
                            onChange={e => set(d => {
                              const all = d.moodboardPieces || {};
                              const prev = all[piece] || {};
                              return {
                                ...d,
                                moodboardPieces: {
                                  ...all,
                                  [piece]: { ...prev, palette: e.target.value },
                                },
                              };
                            })}
                            placeholder="Ex : tons sable, camel, blanc cassé, touches de vert olive…"
                          /></Field><Field label="Matériaux envisagés"><Input
                            value={mood.materiaux || ""}
                            onChange={e => set(d => {
                              const all = d.moodboardPieces || {};
                              const prev = all[piece] || {};
                              return {
                                ...d,
                                moodboardPieces: {
                                  ...all,
                                  [piece]: { ...prev, materiaux: e.target.value },
                                },
                              };
                            })}
                            placeholder="Bois clair, pierre naturelle, lin, laiton brossé…"
                          /></Field></Grid><Grid cols={2}><Field label="Ambiance lumineuse"><Sel
                            value={mood.ambiance || "Chaleureuse"}
                            onChange={e => set(d => {
                              const all = d.moodboardPieces || {};
                              const prev = all[piece] || {};
                              return {
                                ...d,
                                moodboardPieces: {
                                  ...all,
                                  [piece]: { ...prev, ambiance: e.target.value },
                                },
                              };
                            })}
                            options={AMBIANCES}
                          /></Field><Field label="Notes décoration & ameublement"><Input
                            multiline
                            rows={2}
                            value={mood.notes || ""}
                            onChange={e => set(d => {
                              const all = d.moodboardPieces || {};
                              const prev = all[piece] || {};
                              return {
                                ...d,
                                moodboardPieces: {
                                  ...all,
                                  [piece]: { ...prev, notes: e.target.value },
                                },
                              };
                            })}
                            placeholder="Pièces fortes, type de mobilier, niveau de remplissage, ambiance générale…"
                          /></Field></Grid>
                      {/* Photos d'inspiration */}
                      <div style={{ marginTop: "10px" }}><Label>Photos d'inspiration (max. 3)</Label><input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={e => {
                            handleInspirationUpload(piece, e.target.files);
                            e.target.value = "";
                          }}
                          style={{ fontSize: "10px" }}
                        />
                        {photos.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "8px" }}>
                            {photos.map((p) => (
                              <div key={p.id} style={{ border: `1px solid ${C.border}`, borderRadius: "3px", overflow: "hidden", background: C.bg }}><img src={p.url} alt={p.name} style={{ width: "90px", height: "68px", objectFit: "cover", display: "block" }} /><input
                                  type="text"
                                  value={p.note || ""}
                                  onChange={e => set(d => {
                                    const all = d.photosInspiration || {};
                                    const list = (all[piece] || []).map(img =>
                                      img.id === p.id ? { ...img, note: e.target.value } : img
                                    );
                                    return {
                                      ...d,
                                      photosInspiration: {
                                        ...all,
                                        [piece]: list,
                                      },
                                    };
                                  })}
                                  placeholder="Ce que j'aime dans cette photo"
                                  style={{ width: "100%", fontSize: "9px", padding: "4px 6px", border: "none", borderTop: `1px solid ${C.border}`, outline: "none" }}
                                /></div>
                            ))}
                          </div>
                        )}
                      </div></div>
                  );
                })}
              </div></div>
          )}

          {/* Bloc technique existant */}
          <Field label="Contraintes techniques & architecturales"><Input multiline rows={2} value={data.contraintes || ""} onChange={e => set(d => ({ ...d, contraintes: e.target.value }))} placeholder="Poteaux porteurs, plafond bas, humidité, vis-à-vis, orientation, réseaux existants…" /></Field><Field label="Relevé & cotes (notes du laser)"><Input multiline rows={2} value={data.releve || ""} onChange={e => set(d => ({ ...d, releve: e.target.value }))} placeholder="Dimensions principales, hauteurs sous plafond, ouvertures, particularités…" /></Field><UploadZone label="Photos du site (avant travaux)" files={data.photos} onFiles={f => set(d => ({ ...d, photos: typeof f === "function" ? f(d.photos || []) : f }))} /></div></Card>

      {/* ANALYSE DE PLANS — Plans & Documents existants */}
      <Card><SecTitle sub="Importez vos plans existants pour analyse — nous identifierons les contraintes structurelles et les opportunités">
          Plans & Documents existants
        </SecTitle><div style={{ display: "flex", flexDirection: "column", gap: "12px" }}><UploadZone
            label="Plans existants (PDF, DWG, images, SKP, BAK)"
            files={data.plansExistants}
            onFiles={f => set(d => ({ ...d, plansExistants: typeof f === "function" ? f(d.plansExistants || []) : f }))}
            accept=".pdf,.dwg,.png,.jpg,.jpeg,.skp,.bak"
          /><Field label="Notes sur les plans fournis"><Input
              multiline
              rows={3}
              value={data.notesPlans || ""}
              onChange={e => set(d => ({ ...d, notesPlans: e.target.value }))}
              placeholder="Précisez l'état des plans, les zones mises à jour, les éléments qui vous interrogent…"
            /></Field><div style={{ textAlign: "right", marginTop: "6px" }}><Btn
              onClick={async () => {
                setLoadingPlans(true);
                try {
                  const docs = (data.plansExistants || []).slice(0, 4).map(i => ({ type: i.type, data: i.data }));
                  const sys = `Tu es YOMA DESIGN, architecte d'intérieur expert. Analyse les documents fournis et identifie : surfaces approximatives, contraintes structurelles visibles, opportunités d'aménagement, points d'attention particuliers. Sois précis et professionnel.`;
                  const list = (data.plansExistants || []).map(f => `• ${f.name || "Document"} (${f.type || "fichier"})`).join("\\n");
                  const user = `Plans et documents fournis :\\n${list || "Aucun détail spécifique"}\\n\\nNotes du client : ${data.notesPlans || "Aucune note complémentaire."}`;
                  const result = await callAI(sys, user, docs);
                  set(d => ({ ...d, analysePlans: result }));
                } finally {
                  setLoadingPlans(false);
                }
              }}
              variant="primary"
              disabled={loadingPlans || !(data.plansExistants && data.plansExistants.length)}
            >
              {loadingPlans ? "Analyse en cours…" : "Analyser les plans avec l'IA"}
            </Btn></div>
          {(loadingPlans || data.analysePlans) && (
            <AIBox
              content={data.analysePlans}
              loading={loadingPlans}
              title="Analyse des plans existants — YOMA DESIGN"
            />
          )}
        </div></Card><div style={{ textAlign: "center" }}><Btn onClick={generate} variant="gold" disabled={loading || !data.client}>
          {loading ? "Génération…" : "Générer la Note d'Esquisse (ESQ)"}
        </Btn></div>

      {/* MESSAGE DE FIN */}
      <Card style={{ background: C.beige, border: `1px solid ${C.goldLight}`, marginTop: "12px" }}><div style={{ textAlign: "center" }}><div style={{ fontFamily: "'Playfair Display', serif", fontSize: "20px", color: C.taupeDeep, marginBottom: "6px" }}>
            Merci pour votre confiance
          </div><div style={{ fontFamily: "'DM Sans'", fontSize: "12px", color: C.muted, maxWidth: "520px", margin: "0 auto", lineHeight: 1.7 }}>
            Merci d'avoir pris le temps de remplir ce questionnaire. Nous l'utiliserons pour préparer notre rencontre
            et vous proposer un accompagnement sur mesure, fidèle à vos besoins et à votre style.
          </div><div style={{ fontFamily: "'DM Sans'", fontSize: "12px", color: C.gold, marginTop: "10px" }}>
            contact@yomadesign.ma
          </div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: "16px", color: C.taupeDeep, marginTop: "4px", letterSpacing: "0.12em", textTransform: "uppercase" }}>
            YOMA DESIGN
          </div></div></Card>

      {(loading || data.esq) && (
        <><AIBox content={data.esq} loading={loading} title="Note d'Esquisse (ESQ) — YOMA DESIGN" />
          {data.esq && !loading && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}><LivrablerBadge code="ESQ" label="Note d'Esquisse" generated={!!data.esq} /><LivrablerBadge
                code="CDC"
                label="Cahier des Charges"
                generated={
                  !!data.client &&
                  (
                    (data.objectifsProjet && data.objectifsProjet.length > 0) ||
                    !!data.incontournables ||
                    !!data.aExclure
                  )
                }
              /><LivrablerBadge code="PHO" label="Photos de référence" generated={(data.photos?.length || 0) > 0} /></div>
          )}
        </>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   ÉTAPE 2 — Conception Détaillée
══════════════════════════════════════════════════════ */
function Etape2({ data, set, step1 }) {
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    const images = [...(data.plans || []), ...(data.vues3d || [])].slice(0, 4).map(i => ({ type: i.type, data: i.data }));
    const sys = `Tu es YOMA DESIGN. Rédige un document APS/APD professionnel.
Structure :
1. SCÉNARIO RETENU — description du parti pris architectural
2. PLANS 2D — description des implantations validées
3. PALETTE MATÉRIAUX — sols, peintures, revêtements muraux
4. IMPLANTATION LUMIÈRE — points lumineux, ambiances
5. MOBILIER PRINCIPAL — pièce par pièce
6. ESTIMATION BUDGÉTAIRE GLOBALE (détaillée par poste)
7. POINTS DE VIGILANCE`;
    const user = `Projet : ${step1?.client} — ${step1?.typeProjet} — ${step1?.surface} m²
Budget : ${step1?.budget} MAD | Style : ${step1?.style}
Scénario choisi : ${data.scenario}
Matériaux sols : ${data.sols} | Peintures : ${data.peintures}
Luminaires prévus : ${data.luminaires}
Notes APS : ${data.notesAPS}
Notes APD : ${data.notesAPD}
${images.length ? `${images.length} plan(s)/vue(s) 3D joints.` : ""}`;
    const result = await callAI(sys, user, images);
    set(d => ({ ...d, apd: result }));
    setLoading(false);
  };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}><Card><SecTitle sub="Avant-Projet Sommaire — Premier scénario validé">APS — Avant-Projet Sommaire</SecTitle><div style={{ display: "flex", flexDirection: "column", gap: "14px" }}><Field label="Scénario architectural retenu" required><Input multiline rows={3} value={data.scenario || ""} onChange={e => set(d => ({ ...d, scenario: e.target.value }))} placeholder="Décrivez le parti pris : ouverture des espaces, circulation, fil conducteur du design…" /></Field><Field label="Notes APS (plans 2D cotés)"><Input multiline rows={2} value={data.notesAPS || ""} onChange={e => set(d => ({ ...d, notesAPS: e.target.value }))} placeholder="Observations sur l'implantation, les flux, les proportions…" /></Field><UploadZone label="Plans 2D / Croquis" files={data.plans} onFiles={f => set(d => ({ ...d, plans: typeof f === "function" ? f(d.plans || []) : f }))} accept="image/*,.pdf" /><UploadZone label="Premières vues 3D" files={data.vues3d} onFiles={f => set(d => ({ ...d, vues3d: typeof f === "function" ? f(d.vues3d || []) : f }))} /></div></Card><Card><SecTitle sub="Avant-Projet Définitif — Validation finale des matériaux et équipements">APD — Avant-Projet Définitif</SecTitle><div style={{ display: "flex", flexDirection: "column", gap: "14px" }}><Grid cols={2}><Field label="Revêtements de sols validés"><Input value={data.sols || ""} onChange={e => set(d => ({ ...d, sols: e.target.value }))} placeholder="Ex : Zellige blanc 10×10 séjour, marbre Noir Marquina SdB…" /></Field><Field label="Peintures & enduits"><Input value={data.peintures || ""} onChange={e => set(d => ({ ...d, peintures: e.target.value }))} placeholder="Ex : Enduit tadelakt taupe chambre 1, blanc cassé RAL 9001…" /></Field></Grid><Field label="Implantation luminaires"><Input value={data.luminaires || ""} onChange={e => set(d => ({ ...d, luminaires: e.target.value }))} placeholder="Ex : Spots encastrés LED salon, suspension centrale SàM, appliques chambre…" /></Field><Field label="Notes APD & demandes de validation client"><Input multiline rows={2} value={data.notesAPD || ""} onChange={e => set(d => ({ ...d, notesAPD: e.target.value }))} placeholder="Points restants à valider avec le client, arbitrages budgétaires…" /></Field></div></Card><div style={{ textAlign: "center" }}><Btn onClick={generate} variant="gold" disabled={loading || !data.scenario}>
          {loading ? "Génération…" : "Générer le Document APS/APD + Estimation"}
        </Btn></div>
      {(loading || data.apd) && <AIBox content={data.apd} loading={loading} title="APS / APD + Estimation Budgétaire — YOMA DESIGN" />}
      {data.apd && !loading && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }}><LivrablerBadge code="APS" label="Avant-Projet Sommaire" generated /><LivrablerBadge code="APD" label="Avant-Projet Définitif" generated /></div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   ÉTAPE 3 — Dossier Technique & Prescription
══════════════════════════════════════════════════════ */
const CATEGORIES_FFE = ["Mobilier", "Luminaires", "Textiles", "Tapis", "Décoration", "Revêtements", "Sanitaires", "Cuisine", "Menuiserie", "Autre"];

function fmtFFE(n) { return Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
function calcFFE(it, coeff) {
  const pub  = Number(it.prixPublic || it.prix || 0);
  const rem  = Number(it.remisePro || 0);
  const ach  = pub * (1 - rem / 100);
  const cli  = ach * coeff;
  const qty  = Number(it.quantite || it.qte || 1);
  return { pub, rem, ach, cli, qty, total: cli * qty };
}

function Etape3({ data, set, step1 }) {
  const [loading, setLoading]   = useState(false);
  const [ffeView, setFfeView]   = useState("cards");   // "cards" | "table"
  const [ffeUrl, setFfeUrl]     = useState("");
  const [ffeLoad, setFfeLoad]   = useState(false);
  const [ffeErr, setFfeErr]     = useState("");
  const [ffeCoeff, setFfeCoeff] = useState(1.20);
  const [showMarge, setShowMarge] = useState(false);

  const items = data.ffe || [];
  const addItem  = (extra = {}) => set(d => ({ ...d, ffe: [...(d.ffe || []), { id: Date.now() + Math.random(), photo: "", article: "", designation: "", categorie: "Mobilier", source: "", url: "", ref: "", fournisseur: "", dim: "", prixPublic: 0, prix: 0, remisePro: 0, quantite: 1, qte: 1, notes: "", note: "", ...extra }] }));
  const upItem   = (id, f, v)  => set(d => ({ ...d, ffe: d.ffe.map(i => i.id === id ? { ...i, [f]: v } : i) }));
  const rmItem   = (id)        => set(d => ({ ...d, ffe: d.ffe.filter(i => i.id !== id) }));
  const dupItem  = (id)        => { const it = items.find(i => i.id === id); if (it) set(d => ({ ...d, ffe: [...(d.ffe || []), { ...it, id: Date.now() + Math.random() }] })); };
  const totalHT  = items.reduce((s, it) => s + calcFFE(it, ffeCoeff).total, 0);

  // proxy CORS-safe pour images (IKEA, Zara Home, Roche Bobois bloquent le CORS direct)
  const proxyImg = (u) => !u ? "" : `https://images.weserv.nl/?url=${encodeURIComponent(u)}&w=600&output=jpg&il`;

  // extraction prix multi-stratégies
  const extractPriceFFE = (d) => {
    const native = Number(d.price?.amount ?? 0);
    if (native > 0) return native;
    const text = [d.description, d.title, JSON.stringify(d)].filter(Boolean).join(" ").slice(0, 3000);
    const pats = [
      /(\d[\d\s]{1,8}(?:[.,]\d{1,2})?)\s*(?:DH|Dhs?|MAD)/i, /(?:DH|Dhs?|MAD)\s*(\d[\d\s]{1,8}(?:[.,]\d{1,2})?)/i,
      /(?:€|EUR)\s*(\d[\d\s]{1,8}(?:[.,]\d{1,2})?)/i, /(\d[\d\s]{1,8}(?:[.,]\d{1,2})?)\s*(?:€|EUR)/i,
      /"price"\s*:\s*"?(\d+(?:[.,]\d{1,2})?)"?/i,
    ];
    for (const p of pats) { const m = text.match(p); if (m) { const n = parseFloat(m[1].replace(/\s/g,"").replace(",",".")); if (n > 0 && n < 999999) return n; } }
    return 0;
  };

  const scrapeFFE = async () => {
    if (!ffeUrl.trim()) return;
    setFfeLoad(true); setFfeErr("");
    try {
      const raw = ffeUrl.trim();
      // Tentative 1 : sans prerender
      let d = {};
      try {
        const r1 = await fetch(`https://api.microlink.io?url=${encodeURIComponent(raw)}&screenshot=false&meta=true`, { signal: AbortSignal.timeout(8000) });
        const j1 = await r1.json();
        if (j1.status === "success") d = j1.data || {};
      } catch {}
      // Tentative 2 : prerender=true si manque image ou prix
      if (!d.image?.url && !d.price?.amount) {
        try {
          const r2 = await fetch(`https://api.microlink.io?url=${encodeURIComponent(raw)}&screenshot=false&meta=true&prerender=true`, { signal: AbortSignal.timeout(18000) });
          const j2 = await r2.json();
          if (j2.status === "success" && j2.data) d = j2.data;
        } catch {}
      }
      const prix = extractPriceFFE(d);
      const rawPhoto = d.image?.url || d.logo?.url || "";
      const photo = proxyImg(rawPhoto); // proxy CORS
      const src = d.publisher || (new URL(raw).hostname.replace("www.", "").split(".")[0].toUpperCase());
      const rawTitle = (d.title || "Article").replace(/\s*[-|–]\s*(IKEA|Zara Home|Roche Bobois|Maisons du Monde).*$/i, "").slice(0, 80).trim();
      addItem({ photo, article: rawTitle, designation: rawTitle, source: src, fournisseur: src, url: raw, prixPublic: prix, prix });
      setFfeUrl("");
      if (prix === 0) setFfeErr("⚠ Prix non trouvé — saisissez-le manuellement dans le tableau.");
      else setFfeErr("");
    } catch { setFfeErr("Impossible de récupérer ce produit. Ajoutez-le manuellement."); }
    finally { setFfeLoad(false); }
  };

  const generateBookDeco = async () => {
    setLoading(true);
    const sys = `Tu es YOMA DESIGN, expert FF&E. Génère un Book Déco / Shopping List professionnel et exhaustif.
Structure par pièce, puis par catégorie.
Pour chaque article : Désignation précise, Dimensions, Référence suggérée, Fournisseur recommandé (Maroc + international), Prix estimatif MAD, Délai de livraison estimé.
Finir par : RÉCAPITULATIF BUDGÉTAIRE par catégorie et TOTAL FF&E.`;
    const user = `Projet : ${step1?.client} — ${step1?.typeProjet} — ${step1?.surface} m²
Budget : ${step1?.budget} MAD | Style : ${step1?.style} | Pièces : ${step1?.pieces}
Articles sélectionnés :\n${items.map(i => `• ${i.article || i.designation} (${i.categorie}) — ${i.fournisseur || i.source} — ${i.prixPublic || i.prix} MAD × ${i.quantite || i.qte}`).join("\n")}
Notes DCE : ${data.notesDCE || ""}
Menuiserie sur mesure : ${data.menuiserie || ""}`;
    const result = await callAI(sys, user);
    set(d => ({ ...d, bookDeco: result }));
    setLoading(false);
  };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}><Card><SecTitle sub="Plans techniques pour les artisans — Électricité, plomberie, démolition">DCE — Dossier de Consultation des Entreprises</SecTitle><div style={{ display: "flex", flexDirection: "column", gap: "14px" }}><Field label="Notes techniques & descriptif des travaux"><Input multiline rows={3} value={data.notesDCE || ""} onChange={e => set(d => ({ ...d, notesDCE: e.target.value }))} placeholder="Travaux de démolition/reconstruction, modification électrique, plomberie, cloisonnement…" /></Field><UploadZone label="Plans techniques (élec, plomb, démolition)" files={data.plansTech} onFiles={f => set(d => ({ ...d, plansTech: typeof f === "function" ? f(d.plansTech || []) : f }))} accept="image/*,.pdf,.dwg" /></div></Card><Card><SecTitle sub="Dessins précis pour cuisine, dressing, bibliothèques, placards">Carnet de Détails — Menuiserie Sur Mesure</SecTitle><Field label="Description des éléments menuisés"><Input multiline rows={3} value={data.menuiserie || ""} onChange={e => set(d => ({ ...d, menuiserie: e.target.value }))} placeholder="Cuisine sur mesure : L×P×H, finition, quincaillerie. Dressing chambre 1 : …" /></Field><div style={{ marginTop: "14px" }}><UploadZone label="Dessins de détails / plans menuiserie" files={data.plansMenuiserie} onFiles={f => set(d => ({ ...d, plansMenuiserie: typeof f === "function" ? f(d.plansMenuiserie || []) : f }))} accept="image/*,.pdf" /></div></Card><Card>
        {/* ── En-tête ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: 8 }}>
          <SecTitle sub="Shopping List visuelle — Mobilier, luminaires, tapis, déco">Book Déco — Liste FF&E</SecTitle>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {/* Coefficient */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, background: C.beige, borderRadius: 6, padding: "4px 10px" }}>
              <span style={{ fontSize: 11, color: C.muted }}>Coeff.</span>
              <input type="number" step="0.05" min="1" value={ffeCoeff} onChange={e => setFfeCoeff(parseFloat(e.target.value) || 1)}
                style={{ width: 52, border: "none", background: "transparent", fontSize: 13, fontWeight: 700, color: C.gold, textAlign: "center", fontFamily: "'DM Sans'" }} />
            </div>
            {/* Marges */}
            <button onClick={() => setShowMarge(m => !m)} style={{ border: `1px solid ${C.border}`, background: "transparent", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", color: C.muted }}>
              {showMarge ? "🔒 Masquer marges" : "👁 Mes marges"}
            </button>
            {/* Vues */}
            <div style={{ display: "flex", border: `1px solid ${C.border}`, borderRadius: 6, overflow: "hidden" }}>
              {[["cards","⊞"],["table","≡"]].map(([v,ico]) => (
                <button key={v} onClick={() => setFfeView(v)} style={{ border: "none", padding: "4px 10px", cursor: "pointer", fontSize: 13, background: ffeView === v ? C.ink : "transparent", color: ffeView === v ? "#fff" : C.muted, fontWeight: ffeView === v ? 700 : 400 }}>{ico}</button>
              ))}
            </div>
            <Btn onClick={() => addItem()} small variant="ghost">+ Manuel</Btn>
          </div>
        </div>

        {/* ── Scraper URL ── */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <input value={ffeUrl} onChange={e => setFfeUrl(e.target.value)} onKeyDown={e => e.key === "Enter" && scrapeFFE()}
            placeholder="Coller un lien IKEA, Zara Home, Roche Bobois, Maisons du Monde…"
            style={{ flex: 1, border: `1px solid ${C.border}`, borderRadius: 8, padding: "9px 14px", fontSize: 13, fontFamily: "'DM Sans'", color: C.text, background: C.bg }} />
          <button onClick={scrapeFFE} disabled={ffeLoad || !ffeUrl.trim()}
            style={{ background: C.gold, color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 700, cursor: "pointer", fontSize: 13, opacity: (ffeLoad || !ffeUrl.trim()) ? 0.5 : 1 }}>
            {ffeLoad ? "⏳" : "➕ Ajouter"}
          </button>
        </div>
        {ffeErr && <p style={{ color: C.alert, fontSize: 12, margin: "-10px 0 12px" }}>⚠ {ffeErr}</p>}

        {/* ── VUE CARTES ── */}
        {ffeView === "cards" && (
          items.length === 0
          ? <div style={{ textAlign: "center", padding: "32px", color: C.muted, fontSize: 13, fontStyle: "italic" }}>Aucun article — collez un lien ci-dessus ou ajoutez manuellement</div>
          : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }}>
              {items.map(it => {
                const c = calcFFE(it, ffeCoeff);
                return (
                  <div key={it.id} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
                    <div style={{ height: 150, background: C.beige, overflow: "hidden", position: "relative" }}>
                      {it.photo
                        ? <img src={it.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        : <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, color: C.border }}>🛋</div>}
                      {(it.source || it.fournisseur) && <div style={{ position: "absolute", top: 6, right: 6, background: "rgba(28,25,21,.75)", color: C.gold, fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 4 }}>{it.source || it.fournisseur}</div>}
                    </div>
                    <div style={{ padding: "10px 12px" }}>
                      <input value={it.article || it.designation} onChange={e => { upItem(it.id, "article", e.target.value); upItem(it.id, "designation", e.target.value); }}
                        style={{ border: "none", background: "transparent", fontSize: 13, fontWeight: 600, color: C.text, width: "100%", fontFamily: "'DM Sans'", marginBottom: 4 }} />
                      <div style={{ marginBottom: 8 }}>
                        <select value={it.categorie} onChange={e => upItem(it.id, "categorie", e.target.value)}
                          style={{ width: "100%", border: `1px solid ${C.border}`, borderRadius: 4, padding: "3px 8px", fontSize: 11, color: C.muted, background: C.bg, marginBottom: 6 }}>
                          {CATEGORIES_FFE.map(cat => <option key={cat}>{cat}</option>)}
                        </select>
                        {/* Quantité — stepper +/− */}
                        <div style={{ display: "flex", alignItems: "center", border: `1px solid ${C.border}`, borderRadius: 6, overflow: "hidden", background: "#fff" }}>
                          <button onClick={() => { const v = Math.max(1,(Number(it.quantite||it.qte||1))-1); upItem(it.id,"quantite",v); upItem(it.id,"qte",v); }}
                            style={{ width: 30, height: 30, border: "none", background: C.beige, color: C.text, fontSize: 15, cursor: "pointer", fontWeight: 700, flexShrink: 0 }}>−</button>
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <span style={{ fontSize: 8, color: C.muted, fontWeight: 700, letterSpacing: "0.1em" }}>QTÉ</span>
                            <input type="number" min="1" value={it.quantite || it.qte || 1}
                              onChange={e => { const v = Math.max(1, Number(e.target.value)||1); upItem(it.id,"quantite",v); upItem(it.id,"qte",v); }}
                              style={{ width: "100%", border: "none", background: "transparent", fontSize: 13, textAlign: "center", color: C.text, fontWeight: 700, padding: 0, outline: "none" }} />
                          </div>
                          <button onClick={() => { const v = (Number(it.quantite||it.qte||1))+1; upItem(it.id,"quantite",v); upItem(it.id,"qte",v); }}
                            style={{ width: 30, height: 30, border: "none", background: C.beige, color: C.text, fontSize: 15, cursor: "pointer", fontWeight: 700, flexShrink: 0 }}>+</button>
                        </div>
                      </div>
                      <div style={{ background: C.beige, borderRadius: 7, padding: "8px 10px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                          <span style={{ fontSize: 11, color: C.muted }}>Prix public</span>
                          <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
                            <input type="number" min="0" value={it.prixPublic || it.prix || 0} onChange={e => { upItem(it.id, "prixPublic", e.target.value); upItem(it.id, "prix", e.target.value); }}
                              style={{ width: 65, border: "none", background: "transparent", fontSize: 12, textAlign: "right", color: C.muted, fontFamily: "'DM Sans'" }} />
                            <span style={{ fontSize: 11, color: C.muted }}>MAD</span>
                          </div>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                          <span style={{ fontSize: 11, color: C.muted }}>Remise pro</span>
                          <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
                            <input type="number" min="0" max="100" value={it.remisePro || 0} onChange={e => upItem(it.id, "remisePro", e.target.value)}
                              style={{ width: 40, border: "none", background: "transparent", fontSize: 12, textAlign: "right", color: C.muted, fontFamily: "'DM Sans'" }} />
                            <span style={{ fontSize: 11, color: C.muted }}>%</span>
                          </div>
                        </div>
                        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 6, display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>Total</span>
                          <span style={{ fontSize: 15, fontWeight: 700, color: C.taupeDeep }}>{fmtFFE(c.total)} MAD</span>
                        </div>
                        {showMarge && <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                          <span style={{ fontSize: 11, color: C.muted }}>Marge</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: C.gold }}>{fmtFFE(c.total - c.ach * c.qty)} MAD</span>
                        </div>}
                      </div>
                      {it.url && <a href={it.url} target="_blank" rel="noopener noreferrer" style={{ display: "block", marginTop: 6, fontSize: 10, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>🔗 {it.url.slice(0, 40)}…</a>}
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 4, marginTop: 6 }}>
                        <button onClick={() => dupItem(it.id)} title="Dupliquer" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13 }}>📋</button>
                        <button onClick={() => rmItem(it.id)} title="Supprimer" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: C.alert }}>🗑</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
        )}

        {/* ── VUE TABLEAU ── */}
        {ffeView === "table" && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
              <thead><tr style={{ background: C.ink }}>
                {["Visuel","Article","Catégorie","Prix public","Remise %","Prix client","Qté","Total","Note",""].map(h => (
                  <th key={h} style={{ padding: "8px 10px", color: C.gold, fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", whiteSpace: "nowrap", textAlign: "left" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={10} style={{ textAlign: "center", padding: "24px", color: C.muted, fontStyle: "italic" }}>Aucun article</td></tr>}
                {items.map((it, idx) => {
                  const c = calcFFE(it, ffeCoeff);
                  return (
                    <tr key={it.id} style={{ background: idx % 2 === 0 ? C.bgCard : C.bg, borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: 6 }}>{it.photo ? <img src={it.photo} alt="" style={{ width: 52, height: 42, objectFit: "cover", borderRadius: 5 }} /> : <div style={{ width: 52, height: 42, background: C.beige, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🛋</div>}</td>
                      <td style={{ padding: "6px 8px", minWidth: 140 }}>
                        <input value={it.article || it.designation} onChange={e => { upItem(it.id, "article", e.target.value); upItem(it.id, "designation", e.target.value); }} style={{ border: "none", background: "transparent", fontSize: 12, fontWeight: 600, color: C.text, width: "100%", fontFamily: "'DM Sans'" }} />
                        <input value={it.fournisseur || it.source} onChange={e => { upItem(it.id, "fournisseur", e.target.value); upItem(it.id, "source", e.target.value); }} placeholder="Fournisseur" style={{ border: "none", background: "transparent", fontSize: 10, color: C.muted, width: "100%", fontFamily: "'DM Sans'" }} />
                      </td>
                      <td style={{ padding: "6px 8px" }}><select value={it.categorie} onChange={e => upItem(it.id, "categorie", e.target.value)} style={{ border: `1px solid ${C.border}`, borderRadius: 4, padding: "3px 6px", fontSize: 11, background: C.bg }}>{CATEGORIES_FFE.map(cat => <option key={cat}>{cat}</option>)}</select></td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}><input type="number" value={it.prixPublic || it.prix || 0} onChange={e => { upItem(it.id, "prixPublic", e.target.value); upItem(it.id, "prix", e.target.value); }} style={{ border: "none", background: "transparent", width: 70, textAlign: "right", fontSize: 12, fontFamily: "'DM Sans'" }} /></td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}><input type="number" min="0" max="100" value={it.remisePro || 0} onChange={e => upItem(it.id, "remisePro", e.target.value)} style={{ border: "none", background: "transparent", width: 40, textAlign: "right", fontSize: 12, fontFamily: "'DM Sans'" }} />%</td>
                      <td style={{ padding: "6px 8px", textAlign: "right", color: C.taupeDeep, fontWeight: 600 }}>{fmtFFE(c.cli)} MAD</td>
                      <td style={{ padding: "6px 8px", textAlign: "center" }}><input type="number" min="1" value={it.quantite || it.qte || 1} onChange={e => { upItem(it.id, "quantite", e.target.value); upItem(it.id, "qte", e.target.value); }} style={{ border: "none", background: "transparent", width: 36, textAlign: "center", fontSize: 12, fontFamily: "'DM Sans'" }} /></td>
                      <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700, color: C.text }}>{fmtFFE(c.total)} MAD</td>
                      <td style={{ padding: "6px 8px" }}><input value={it.notes || it.note} onChange={e => { upItem(it.id, "notes", e.target.value); upItem(it.id, "note", e.target.value); }} placeholder="Note" style={{ border: "none", background: "transparent", width: 80, fontSize: 11, color: C.muted, fontFamily: "'DM Sans'" }} /></td>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                        <button onClick={() => dupItem(it.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12 }}>📋</button>
                        <button onClick={() => rmItem(it.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.alert, fontSize: 12 }}>🗑</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Total ── */}
        {items.length > 0 && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 32, marginTop: 12, padding: "12px 16px", background: C.beige, borderRadius: 8 }}>
            {showMarge && <div style={{ textAlign: "right" }}><div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.12em", color: C.muted }}>Marge agence</div><div style={{ fontFamily: "'Playfair Display'", fontSize: 18, color: C.gold, fontWeight: 600 }}>{fmtFFE(items.reduce((s,it)=>{ const c=calcFFE(it,ffeCoeff); return s + c.total - c.ach*c.qty; },0))} MAD</div></div>}
            <div style={{ textAlign: "right" }}><div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.12em", color: C.muted }}>Total FF&E HT</div><div style={{ fontFamily: "'Playfair Display'", fontSize: 22, color: C.taupeDeep, fontWeight: 600 }}>{fmtFFE(totalHT)} MAD</div></div>
          </div>
        )}
      </Card><div style={{ textAlign: "center" }}><Btn onClick={generateBookDeco} variant="gold" disabled={loading}>
          {loading ? "Génération…" : "Générer le Book Déco Complet (IA)"}
        </Btn></div>
      {(loading || data.bookDeco) && <AIBox content={data.bookDeco} loading={loading} title="Book Déco / Shopping List — YOMA DESIGN" />}
      {data.bookDeco && !loading && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}><LivrablerBadge code="DCE" label="Dossier Consultation Entreprises" generated={(data.plansTech?.length || 0) > 0} /><LivrablerBadge code="CD" label="Carnet de Détails Menuiserie" generated={!!data.menuiserie} /><LivrablerBadge code="BOOK" label="Book Déco / Shopping List" generated={!!data.bookDeco} /></div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   ÉTAPE 4 — Consultation & Commandes
══════════════════════════════════════════════════════ */
function Etape4({ data, set }) {
  const [loading, setLoading] = useState(false);
  const devis = data.devis || [];

  const addDevis = () => set(d => ({ ...d, devis: [...(d.devis || []), { id: Date.now(), corps: "", entreprise: "", montant: "", delai: "", garantie: "", references: "", notes: "", statut: "Reçu" }] }));
  const upDevis = (id, f, v) => set(d => ({ ...d, devis: d.devis.map(i => i.id === id ? { ...i, [f]: v } : i) }));
  const rmDevis = (id) => set(d => ({ ...d, devis: d.devis.filter(i => i.id !== id) }));

  const CORPS = ["Maçonnerie", "Électricité", "Plomberie", "Peinture", "Revêtements", "Menuiserie bois", "Menuiserie alu", "Climatisation", "Domotique", "Paysagisme", "Déménagement", "Autre"];

  const generate = async () => {
    setLoading(true);
    const sys = `Tu es YOMA DESIGN, coordinateur OPC expérimenté.
Analyse les devis des entreprises et produis un rapport de consultation structuré :
1. TABLEAU COMPARATIF par corps d'état
2. ANALYSE RISQUES & FIABILITÉ pour chaque entreprise
3. RECOMMANDATIONS DE SÉLECTION argumentées
4. PLANNING PRÉVISIONNEL d'intervention des corps d'état
5. POINTS DE NÉGOCIATION identifiés
6. BUDGET TRAVAUX TOTAL recommandé`;
    const user = devis.map(d => `Corps : ${d.corps} | Entreprise : ${d.entreprise} | Montant : ${d.montant} MAD | Délai : ${d.delai} | Garantie : ${d.garantie} | Références : ${d.references} | Notes : ${d.notes}`).join("\n");
    const result = await callAI(sys, "Devis reçus :\n" + user);
    set(d => ({ ...d, analyse: result }));
    setLoading(false);
  };

  const STATUTS = ["Reçu", "En analyse", "Retenu", "Refusé"];
  const STATUT_COL = { Reçu: C.info, "En analyse": C.gold, Retenu: C.success, Refusé: C.alert };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}><Card><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}><SecTitle sub="Réception et analyse des devis artisans par corps d'état">Appel d'Offres — Devis Artisans</SecTitle><Btn onClick={addDevis} small variant="ghost">+ Devis</Btn></div><div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {devis.length === 0 && <div style={{ textAlign: "center", padding: "24px", color: C.muted, fontSize: "12px", fontStyle: "italic" }}>Aucun devis — cliquez "+ Devis" pour saisir les offres reçues</div>}
          {devis.map((d, idx) => (
            <div key={d.id} style={{ border: `1px solid ${STATUT_COL[d.statut] || C.border}30`, borderLeft: `3px solid ${STATUT_COL[d.statut] || C.border}`, borderRadius: "3px", padding: "16px", background: C.bg, position: "relative" }}><button onClick={() => rmDevis(d.id)} style={{ position: "absolute", top: "10px", right: "10px", background: "none", border: "none", cursor: "pointer", color: C.alert, fontSize: "16px" }}>×</button><Grid cols={2}><Field label="Corps d'état"><Sel value={d.corps} onChange={e => upDevis(d.id, "corps", e.target.value)} options={CORPS} /></Field><Field label="Entreprise / Artisan"><Input value={d.entreprise} onChange={e => upDevis(d.id, "entreprise", e.target.value)} placeholder="Raison sociale…" /></Field></Grid><div style={{ marginTop: "10px" }}><Grid cols={4}><Field label="Montant HT (MAD)"><Input value={d.montant} onChange={e => upDevis(d.id, "montant", e.target.value)} placeholder="0" /></Field><Field label="Délai d'exécution"><Input value={d.delai} onChange={e => upDevis(d.id, "delai", e.target.value)} placeholder="Ex : 6 semaines" /></Field><Field label="Garantie"><Input value={d.garantie} onChange={e => upDevis(d.id, "garantie", e.target.value)} placeholder="Ex : 2 ans" /></Field><Field label="Statut"><Sel value={d.statut} onChange={e => upDevis(d.id, "statut", e.target.value)} options={STATUTS} /></Field></Grid></div><div style={{ marginTop: "10px" }}><Grid cols={2}><Field label="Références chantiers"><Input value={d.references} onChange={e => upDevis(d.id, "references", e.target.value)} placeholder="Projets similaires réalisés…" /></Field><Field label="Observations"><Input value={d.notes} onChange={e => upDevis(d.id, "notes", e.target.value)} placeholder="Points forts, risques identifiés…" /></Field></Grid></div></div>
          ))}
        </div></Card><Card><SecTitle sub="Suivi des commandes mobilier et luminaires (délais 8–12 semaines)">Gestion des Achats FF&E</SecTitle><Field label="État des commandes en cours"><Input multiline rows={3} value={data.commandes || ""} onChange={e => set(d => ({ ...d, commandes: e.target.value }))} placeholder="Ex : Canapé Poliform — commandé le 15/03 — livraison prévue 15/06. Luminaires Flos — en attente confirmation stock…" /></Field><div style={{ marginTop: "14px" }}><Field label="Notes passation de marchés"><Input multiline rows={2} value={data.notesMarchés || ""} onChange={e => set(d => ({ ...d, notesMarchés: e.target.value }))} placeholder="Conditions particulières, avenants, réserves à la signature…" /></Field></div></Card>

      {devis.length >= 2 && (
        <div style={{ textAlign: "center" }}><Btn onClick={generate} variant="gold" disabled={loading}>
            {loading ? "Analyse…" : "Analyse Comparative IA — Recommandations"}
          </Btn></div>
      )}
      {(loading || data.analyse) && <AIBox content={data.analyse} loading={loading} title="Rapport de Consultation — Recommandations YOMA" />}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   ÉTAPE 5 — Suivi de Chantier
══════════════════════════════════════════════════════ */
function Etape5({ data, set, step1 }) {
  const [loading, setLoading] = useState(false);
  const visites = data.visites || [];

  const addVisite = () => set(d => ({ ...d, visites: [...(d.visites || []), { id: Date.now(), date: "", corps: "", avancement: "", observations: "", reserves: "", photos: [] }] }));
  const upVisite = (id, f, v) => set(d => ({ ...d, visites: d.visites.map(i => i.id === id ? { ...i, [f]: v } : i) }));
  const rmVisite = (id) => set(d => ({ ...d, visites: d.visites.filter(i => i.id !== id) }));

  const generate = async () => {
    setLoading(true);
    const lastVisite = visites[visites.length - 1];
    const sys = `Tu es YOMA DESIGN. Rédige un Compte-Rendu de Visite de Chantier professionnel hebdomadaire.
Structure imposée :
1. EN-TÊTE (projet, date, intervenants)
2. AVANCEMENT PAR CORPS D'ÉTAT (% réalisé, qualité d'exécution)
3. RÉSERVES & NON-CONFORMITÉS identifiées
4. DÉCISIONS PRISES en réunion de chantier
5. ACTIONS À MENER — responsable + délai
6. ÉTAT DES COMMANDES FF&E
7. PLANNING PRÉVISIONNEL mis à jour
8. PROCHAINE VISITE prévue
Ton : directeur de chantier rigoureux, précis, factuel.`;
    const user = `Projet : ${step1?.client} — ${step1?.typeProjet}
Visite N° ${visites.length} | Date : ${lastVisite?.date || "À définir"}
Corps présents : ${lastVisite?.corps || ""}
Avancement constaté : ${lastVisite?.avancement || ""}
Observations : ${lastVisite?.observations || ""}
Réserves : ${lastVisite?.reserves || ""}
État commandes FF&E : ${data.etatCommandes || ""}
Notes générales chantier : ${data.notesChantier || ""}`;
    const result = await callAI(sys, user);
    set(d => ({ ...d, cr: result }));
    setLoading(false);
  };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}><Card><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}><SecTitle sub="Direction de l'Exécution des Travaux — DET">Visites de Chantier</SecTitle><Btn onClick={addVisite} small variant="ghost">+ Visite</Btn></div><div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {visites.length === 0 && <div style={{ textAlign: "center", padding: "24px", color: C.muted, fontSize: "12px", fontStyle: "italic" }}>Aucune visite enregistrée — cliquez "+ Visite" pour saisir un rapport</div>}
          {visites.map((v, idx) => (
            <div key={v.id} style={{ border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.taupe}`, borderRadius: "3px", padding: "16px", background: C.bg, position: "relative" }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}><span style={{ fontFamily: "'DM Sans'", fontSize: "11px", fontWeight: 600, color: C.taupe }}>Visite N° {idx + 1}</span><button onClick={() => rmVisite(v.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.alert, fontSize: "14px" }}>×</button></div><Grid cols={2}><Field label="Date de visite"><Input type="date" value={v.date} onChange={e => upVisite(v.id, "date", e.target.value)} /></Field><Field label="Corps d'état présents"><Input value={v.corps} onChange={e => upVisite(v.id, "corps", e.target.value)} placeholder="Maçon, peintre, électricien…" /></Field></Grid><div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "10px" }}><Field label="Avancement constaté"><Input multiline rows={2} value={v.avancement} onChange={e => upVisite(v.id, "avancement", e.target.value)} placeholder="% d'avancement par poste, qualité d'exécution…" /></Field><Field label="Observations & non-conformités"><Input multiline rows={2} value={v.observations} onChange={e => upVisite(v.id, "observations", e.target.value)} placeholder="Écarts par rapport aux plans, malfaçons constatées…" /></Field><Field label="Réserves émises"><Input value={v.reserves} onChange={e => upVisite(v.id, "reserves", e.target.value)} placeholder="Réserves à lever avant paiement…" /></Field></div></div>
          ))}
        </div></Card><Card><SecTitle sub="Coordination et état des commandes en cours de fabrication">État des Commandes & Menuiserie</SecTitle><div style={{ display: "flex", flexDirection: "column", gap: "12px" }}><Field label="État des commandes FF&E (livraisons, retards)"><Input multiline rows={2} value={data.etatCommandes || ""} onChange={e => set(d => ({ ...d, etatCommandes: e.target.value }))} placeholder="Mobilier : confirmé livraison semaine 22. Menuiserie cuisine : en atelier, livraison J+45…" /></Field><Field label="Pose menuiserie & suivi atelier"><Input multiline rows={2} value={data.notesMenuiserie || ""} onChange={e => set(d => ({ ...d, notesMenuiserie: e.target.value }))} placeholder="Avancement fabrication, date de pose prévue, ajustements demandés…" /></Field><Field label="Notes générales chantier"><Input multiline rows={2} value={data.notesChantier || ""} onChange={e => set(d => ({ ...d, notesChantier: e.target.value }))} placeholder="Décisions prises, problèmes résolus, points en attente…" /></Field></div></Card>

      {visites.length > 0 && (
        <div style={{ textAlign: "center" }}><Btn onClick={generate} variant="gold" disabled={loading}>
            {loading ? "Rédaction…" : "Générer le CR de Chantier Hebdomadaire"}
          </Btn></div>
      )}
      {(loading || data.cr) && <AIBox content={data.cr} loading={loading} title="Compte-Rendu de Chantier (DET) — YOMA DESIGN" />}
      {data.cr && !loading && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }}><LivrablerBadge code="DET" label="Direction Exécution Travaux" generated /><LivrablerBadge code="CR" label={`${visites.length} Compte${visites.length > 1 ? "s" : ""}-rendu${visites.length > 1 ? "s" : ""} de visite`} generated /></div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   ÉTAPE 6 — Installation & Mise en Scène
══════════════════════════════════════════════════════ */
const CHECKLIST_DEFAULT = [
  { cat: "Réception travaux", items: ["Conformité générale par rapport aux plans", "Qualité des peintures et enduits", "Pose des revêtements de sol", "Installation électrique complète et testée", "Plomberie — robinetterie et évacuations", "Menuiserie sur mesure posée et ajustée", "Nettoyage fin de chantier effectué"] },
  { cat: "Livraison & Montage mobilier", items: ["Réception et contrôle de tous les colis", "Montage et placement selon plan d'implantation", "Luminaires installés et fonctionnels", "Textiles posés (rideaux, tapis, coussins)", "Mobilier de jardin installé"] },
  { cat: "Styling & Mise en scène", items: ["Disposition des objets décoratifs", "Pose des cadres et tableaux", "Plantes et végétaux en place", "Réglage des éclairages d'ambiance", "Vérification esthétique globale — photo finale"] },
  { cat: "Remise des clés", items: ["DOE constitué et remis au client", "Notices et garanties fournisseurs remises", "Codes d'accès domotique communiqués", "Satisfaction client confirmée", "PV de réception signé"] },
];

function Etape6({ data, set, step1 }) {
  const [loading, setLoading] = useState(false);

  const initChecklist = () => CHECKLIST_DEFAULT.flatMap(c =>
    c.items.map((label, i) => ({ id: `${c.cat}-${i}`, cat: c.cat, label, done: false, reserve: "" }))
  );

  const checklist = data.checklist || initChecklist();
  const toggle = (id) => set(d => ({ ...d, checklist: (d.checklist || initChecklist()).map(c => c.id === id ? { ...c, done: !c.done } : c) }));
  const setReserve = (id, v) => set(d => ({ ...d, checklist: (d.checklist || initChecklist()).map(c => c.id === id ? { ...c, reserve: v } : c) }));

  const done = checklist.filter(c => c.done).length;
  const total = checklist.length;
  const pct = Math.round((done / total) * 100);

  const cats = [...new Set(checklist.map(c => c.cat))];

  const generate = async () => {
    setLoading(true);
    const sys = `Tu es YOMA DESIGN. Rédige un Dossier des Ouvrages Exécutés (DOE) et un Procès-Verbal de Réception complets et professionnels.
Structure :
1. PROCÈS-VERBAL DE RÉCEPTION — date, parties, déclaration de réception
2. ÉTAT DES LIEUX FINAL — description complète de chaque espace livré
3. RÉSERVES ÉMISES (le cas échéant) — délais de levée
4. LISTE DES DOCUMENTS REMIS — plans, garanties, notices
5. DOE — liste des ouvrages exécutés avec descriptif technique
6. ENGAGEMENTS & GARANTIES des entreprises
7. SIGNATURES
Ton : document légal, formel, irréprochable.`;
    const reserves = checklist.filter(c => c.reserve).map(c => `• ${c.label} : ${c.reserve}`).join("\n");
    const user = `Projet : ${step1?.client} — ${step1?.typeProjet} — ${step1?.surface} m²
Points validés : ${done}/${total} (${pct}%)
Réserves identifiées :\n${reserves || "Aucune réserve"}
Observations générales : ${data.observations || "RAS"}
Date de livraison : ${data.dateLivraison || new Date().toLocaleDateString("fr-FR")}`;
    const result = await callAI(sys, user);
    set(d => ({ ...d, doe: result }));
    setLoading(false);
  };

  return (
    <div className="step-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}><Card><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}><SecTitle sub="Contrôle complet avant remise des clés">Checklist de Réception</SecTitle><div style={{ display: "flex", alignItems: "center", gap: "12px" }}><div style={{ fontFamily: "'Playfair Display'", fontSize: "20px", color: pct === 100 ? C.success : C.taupe }}>
              {done}/{total}
            </div><Badge color={pct === 100 ? C.success : C.gold}>{pct}% validé</Badge></div></div><div style={{ height: "4px", background: C.beige, borderRadius: "2px", marginBottom: "20px" }}><div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? C.success : C.gold, borderRadius: "2px", transition: "width .4s ease" }} /></div>

        {cats.map(cat => (
          <div key={cat} style={{ marginBottom: "20px" }}><div style={{ fontFamily: "'DM Sans'", fontSize: "10px", fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: "10px", paddingBottom: "6px", borderBottom: `1px solid ${C.border}` }}>
              {cat}
            </div><div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {checklist.filter(c => c.cat === cat).map(c => (
                <div key={c.id} style={{ display: "flex", gap: "10px", alignItems: "flex-start", padding: "9px 12px", background: c.done ? C.successLight : C.bg, border: `1px solid ${c.done ? C.success + "40" : C.border}`, borderRadius: "3px", transition: "all .2s" }}><div onClick={() => toggle(c.id)} style={{ width: "16px", height: "16px", minWidth: "16px", borderRadius: "2px", border: `2px solid ${c.done ? C.success : C.taupeLight}`, background: c.done ? C.success : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: C.white, fontSize: "10px", transition: "all .2s", marginTop: "1px" }}>
                    {c.done ? "✓" : ""}
                  </div><div style={{ flex: 1 }}><div style={{ fontFamily: "'DM Sans'", fontSize: "12px", color: c.done ? C.success : C.text, textDecoration: c.done ? "none" : "none" }}>{c.label}</div>
                    {!c.done && (
                      <input value={c.reserve} onChange={e => setReserve(c.id, e.target.value)} placeholder="Réserve éventuelle…" style={{ marginTop: "4px", border: "none", background: "transparent", fontSize: "10px", color: C.muted, width: "100%", fontFamily: "'DM Sans'" }} />
                    )}
                  </div></div>
              ))}
            </div></div>
        ))}
      </Card><Card><SecTitle sub="Informations finales pour le DOE">Remise des Clés</SecTitle><Grid cols={2}><Field label="Date de livraison"><Input value={data.dateLivraison || ""} onChange={e => set(d => ({ ...d, dateLivraison: e.target.value }))} placeholder={new Date().toLocaleDateString("fr-FR")} /></Field><Field label="Satisfaction client"><Sel value={data.satisfaction || "Excellente"} onChange={e => set(d => ({ ...d, satisfaction: e.target.value }))} options={["Excellente", "Très satisfait", "Satisfait", "Réserves exprimées"]} /></Field></Grid><div style={{ marginTop: "14px" }}><Field label="Observations générales de réception"><Input multiline rows={3} value={data.observations || ""} onChange={e => set(d => ({ ...d, observations: e.target.value }))} placeholder="Commentaires de fin de chantier, points de satisfaction, éléments à noter…" /></Field></div></Card><div style={{ textAlign: "center" }}><Btn onClick={generate} variant="gold" disabled={loading}>
          {loading ? "Rédaction…" : "Générer le PV de Réception + DOE Complet"}
        </Btn></div>
      {(loading || data.doe) && <AIBox content={data.doe} loading={loading} title="PV de Réception + DOE — YOMA DESIGN" />}
      {data.doe && !loading && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }}><LivrablerBadge code="PV" label="Procès-Verbal de Réception" generated /><LivrablerBadge code="DOE" label="Dossier des Ouvrages Exécutés" generated /></div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   VALIDATION MODAL
══════════════════════════════════════════════════════ */
function ValidationModal({ etape, onConfirm, onCancel }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(30,21,16,.75)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}><div style={{ background: C.bgCard, borderRadius: "6px", maxWidth: "480px", width: "100%", padding: "40px", textAlign: "center", boxShadow: "0 24px 64px rgba(0,0,0,.25)" }}><div style={{ fontFamily: "'Playfair Display'", fontSize: "36px", color: C.gold, marginBottom: "8px" }}>◆</div><div style={{ fontFamily: "'Playfair Display'", fontSize: "22px", color: C.taupeDeep, marginBottom: "8px" }}>
          Valider l'Étape {etape.id}
        </div><div style={{ fontFamily: "'DM Sans'", fontSize: "13px", color: C.muted, marginBottom: "8px" }}>{etape.label}</div><div style={{ fontFamily: "'DM Sans'", fontSize: "12px", color: C.text, lineHeight: 1.7, marginBottom: "8px", padding: "16px", background: C.bgDeep, borderRadius: "3px" }}>
          En validant cette étape, vous confirmez que le livrable <strong style={{ color: C.taupeDeep }}>«{etape.livrable}»</strong> est finalisé et approuvé.
        </div><div style={{ fontFamily: "'DM Sans'", fontSize: "11px", color: C.alert, marginBottom: "24px", padding: "10px", background: C.alertLight, borderRadius: "3px" }}>
           Cette action débloquera l'étape suivante. La progression ne peut pas être annulée.
        </div><div style={{ display: "flex", gap: "12px", justifyContent: "center" }}><Btn onClick={onCancel} variant="ghost">Annuler</Btn><Btn onClick={onConfirm} variant="success">✓ Confirmer la Validation</Btn></div></div></div>
  );
}

/* ══════════════════════════════════════════════════════
   MAIN APP
══════════════════════════════════════════════════════ */
export default function YomaProjet() {
  const [activeStep, setActiveStep] = useState(1);
  const [validated, setValidated] = useState({}); // {1: true, 2: true, ...}
  const [showModal, setShowModal] = useState(null); // etape id
  const [stepsData, setStepsData] = useState({
    1: {
      client: "",
      typeProjet: "Villa neuve",
      budget: "",
      surface: "",
      style: "Contemporain",
      pieces: "",
      modeVie: "",
      contraintes: "",
      releve: "",
      photos: [],
      esq: "",
      piecesPrioritaires: {},
      piecesExterieur: {},
      objectifsProjet: [],
      canalCom: "",
      delaiSouhaite: "",
      contactProjet: "",
      incontournables: "",
      aExclure: "",
      moodboardPieces: {},
      photosInspiration: {},
      plansExistants: [],
      notesPlans: "",
      analysePlans: "",
    },
    2: { scenario: "", notesAPS: "", notesAPD: "", sols: "", peintures: "", luminaires: "", plans: [], vues3d: [], apd: "" },
    3: { notesDCE: "", menuiserie: "", plansTech: [], plansMenuiserie: [], ffe: [], bookDeco: "" },
    4: { devis: [], commandes: "", notesMarchés: "", analyse: "" },
    5: { visites: [], etatCommandes: "", notesMenuiserie: "", notesChantier: "", cr: "" },
    6: { checklist: null, dateLivraison: "", satisfaction: "Excellente", observations: "", doe: "" },
  });

  const setStep = (n) => (updater) => setStepsData(d => ({ ...d, [n]: typeof updater === "function" ? updater(d[n]) : updater }));
  const isUnlocked = (id) => id === 1 || validated[id - 1];
  const isValidated = (id) => validated[id];

  const handleValidate = (id) => {
    setValidated(v => ({ ...v, [id]: true }));
    setShowModal(null);
    if (id < 6) setTimeout(() => setActiveStep(id + 1), 300);
  };

  const currentEtape = ETAPES.find(e => e.id === activeStep);

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}><Styles />

      {showModal && (
        <ValidationModal
          etape={ETAPES.find(e => e.id === showModal)}
          onConfirm={() => handleValidate(showModal)}
          onCancel={() => setShowModal(null)}
        />
      )}

      {/* HEADER */}
      <div style={{ background: C.taupeDeep, padding: "0 32px" }}><div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 0", borderBottom: `1px solid rgba(255,255,255,.08)` }}><div style={{ display: "flex", alignItems: "baseline", gap: "16px" }}><div style={{ fontFamily: "'Playfair Display'", fontSize: "20px", color: C.white, fontWeight: 400, letterSpacing: "0.12em" }}>YOMA DESIGN</div><div style={{ fontFamily: "'DM Sans'", fontSize: "9px", color: C.taupeLight, letterSpacing: "0.2em", textTransform: "uppercase" }}>Mission Architecture Intérieure</div></div>
          {stepsData[1].client && (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}><div style={{ fontFamily: "'Playfair Display'", fontSize: "14px", color: C.goldLight, fontStyle: "italic" }}>{stepsData[1].client}</div>
              {stepsData[1].typeProjet && <Badge color={C.goldLight}>{stepsData[1].typeProjet}</Badge>}
              {stepsData[1].surface && <Badge color={C.taupeLight}>{stepsData[1].surface} m²</Badge>}
            </div>
          )}
        </div>

        {/* STEPPER */}
        <div style={{ display: "flex", overflowX: "auto", paddingBottom: "0" }}>
          {ETAPES.map((e) => {
            const unlocked = isUnlocked(e.id);
            const validated_ = isValidated(e.id);
            const active = activeStep === e.id;
            return (
              <div
                key={e.id}
                onClick={() => unlocked && setActiveStep(e.id)}
                style={{
                  flex: "1 0 auto", minWidth: "120px", padding: "14px 8px", cursor: unlocked ? "pointer" : "not-allowed",
                  borderBottom: active ? `3px solid ${C.gold}` : "3px solid transparent",
                  opacity: unlocked ? 1 : 0.4, transition: "all .2s", textAlign: "center",
                }}
              ><div style={{ fontSize: "14px", marginBottom: "4px", color: validated_ ? C.success : active ? C.gold : C.taupeLight }}>
                  {validated_ ? "✓" : unlocked ? e.icon : "—"}
                </div><div style={{ fontFamily: "'DM Sans'", fontSize: "9px", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: active ? C.white : validated_ ? C.success : C.taupeLight }}>
                  {e.short}
                </div><div style={{ fontFamily: "'DM Sans'", fontSize: "8px", color: C.taupeLight, marginTop: "2px", opacity: .7 }}>
                  {e.code}
                </div></div>
            );
          })}
        </div></div>

      {/* CONTENT */}
      <div style={{ maxWidth: "960px", margin: "0 auto", padding: "32px 24px" }}>

        {/* STEP HEADER */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "28px" }}><div><div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "6px" }}><span style={{ fontFamily: "'Playfair Display'", fontSize: "28px", color: currentEtape.color, fontWeight: 400 }}>{currentEtape.icon}</span><div><div style={{ fontFamily: "'Playfair Display'", fontSize: "26px", color: C.taupeDeep, fontWeight: 500, lineHeight: 1.1 }}>{currentEtape.label}</div><div style={{ fontFamily: "'DM Sans'", fontSize: "11px", color: C.muted, marginTop: "4px" }}>{currentEtape.subtitle}</div></div></div><div style={{ fontFamily: "'DM Sans'", fontSize: "12px", color: C.muted, maxWidth: "520px", lineHeight: 1.6 }}>{currentEtape.description}</div></div><div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" }}><Badge color={currentEtape.color}>Étape {activeStep} / {ETAPES.length}</Badge>
            {isValidated(activeStep) && <Badge color={C.success}>✓ Validée</Badge>}
          </div></div>

        {/* LOCKED STATE */}
        {!isUnlocked(activeStep) && (
          <div style={{ textAlign: "center", padding: "64px 32px", background: C.lockedBg, border: `2px dashed ${C.border}`, borderRadius: "6px" }}><div style={{ fontFamily: "'Playfair Display'", fontSize: "48px", color: C.locked, marginBottom: "16px" }}>—</div><div style={{ fontFamily: "'Playfair Display'", fontSize: "22px", color: C.muted, marginBottom: "8px" }}>Étape verrouillée</div><div style={{ fontFamily: "'DM Sans'", fontSize: "13px", color: C.muted, maxWidth: "360px", margin: "0 auto", lineHeight: 1.7 }}>
              Cette étape sera débloquée après validation de l'étape précédente — <strong>«{ETAPES[activeStep - 2]?.label}»</strong></div></div>
        )}

        {/* VALIDATED BANNER */}
        {isValidated(activeStep) && isUnlocked(activeStep) && (
          <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px 20px", background: C.successLight, border: `1px solid ${C.success}40`, borderRadius: "3px", marginBottom: "20px" }}><span style={{ fontSize: "18px" }}>✓</span><div><div style={{ fontFamily: "'DM Sans'", fontSize: "12px", fontWeight: 600, color: C.success }}>Étape validée — Livrable «{currentEtape.livrable}» confirmé</div><div style={{ fontFamily: "'DM Sans'", fontSize: "11px", color: C.muted }}>Vous pouvez consulter le contenu mais la progression est verrouillée.</div></div></div>
        )}

        {/* STEP CONTENT */}
        {isUnlocked(activeStep) && (
          <>
            {activeStep === 1 && <Etape1 data={stepsData[1]} set={setStep(1)} />}
            {activeStep === 2 && <Etape2 data={stepsData[2]} set={setStep(2)} step1={stepsData[1]} />}
            {activeStep === 3 && <Etape3 data={stepsData[3]} set={setStep(3)} step1={stepsData[1]} />}
            {activeStep === 4 && <Etape4 data={stepsData[4]} set={setStep(4)} />}
            {activeStep === 5 && <Etape5 data={stepsData[5]} set={setStep(5)} step1={stepsData[1]} />}
            {activeStep === 6 && <Etape6 data={stepsData[6]} set={setStep(6)} step1={stepsData[1]} />}
          </>
        )}

        {/* NAVIGATION */}
        {isUnlocked(activeStep) && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "36px", paddingTop: "24px", borderTop: `1px solid ${C.border}` }}><Btn onClick={() => setActiveStep(s => Math.max(1, s - 1))} variant="ghost" disabled={activeStep === 1}>
              ← Étape précédente
            </Btn><div style={{ display: "flex", gap: "12px" }}>
              {!isValidated(activeStep) && (
                <Btn
                  onClick={() => setShowModal(activeStep)}
                  variant="success"
                  style={{ padding: "12px 28px", fontSize: "11px" }}
                >
                  ✓ {currentEtape.validationLabel}
                </Btn>
              )}
              {activeStep < 6 && isValidated(activeStep) && (
                <Btn onClick={() => setActiveStep(s => Math.min(6, s + 1))} variant="primary">
                  Étape suivante →
                </Btn>
              )}
            </div></div>
        )}
      </div></div>
  );
}
